import React, { useRef, useState, useEffect, useCallback } from "react";
import { VENUE_VIEWBOX, VENUE_HALLS } from "./venueHalls";
import { DOC_BASE_URL } from "../../../../utils/constants";

// Kept on the server per exhibition, beside that exhibition's hall layouts
// (itmeems_node document/Svgtogeojson/exhibition_<id>/, the folder
// getHallLayout reads; served statically under /document), so it can be
// replaced without a frontend build. At several MB it should not go through the
// bundler anyway.
const venueLayoutUrlFor = (exhibitionId) =>
  `${DOC_BASE_URL}/document/Svgtogeojson/exhibition_${exhibitionId}/venue-layout.svg`;

// The whole venue, one status rectangle per hall.
//
// The venue drawing is a multi-MB SVG, so it is drawn as an SVG <image> - the
// browser rasterises it off the React tree - inside the light overlay SVG that
// carries the hall rectangles (see venueHalls.js). Pan by dragging,
// zoom with the wheel, double-click to fit.
const MIN_SCALE = 1;
const MAX_SCALE = 12;

// Text, strokes and paddings below were tuned on a 3370-unit-wide drawing;
// U keeps them the same size relative to whatever drawing is served.
const U = VENUE_VIEWBOX.width / 3370;

// A hall or zone with nothing allocated is white, as an unallocated stall
// reads on the hall plan; only its outline is drawn in a neutral tone so the
// shape stays visible against the drawing.
const UNALLOCATED_FILL = "#FFFFFF";
const UNALLOCATED_STROKE = "#94A3B8";
const strokeFor = (fill) => (fill.toUpperCase() === UNALLOCATED_FILL ? UNALLOCATED_STROKE : fill);

// A hall's outline: its polygon when it is not a plain rectangle (Hall 17's
// L shape), otherwise its box.
const HallShape = ({ hall, ...props }) =>
  hall.points ? (
    <polygon points={hall.points.map(([px, py]) => `${px},${py}`).join(" ")} {...props} />
  ) : (
    <rect x={hall.x} y={hall.y} width={hall.w} height={hall.h} {...props} />
  );

const VenueView = ({
  height = 500,
  statusOfHall,            // (hallName) => status string | null
  getStatusColor,
  selectedHallName = "",
  onHallClick,             // (venueHall) => void
  // Venue Zone View: [{ id, label, box: {x, y, w, h}, status, hallCount }] in
  // venue units. Given, the zones are the status shapes and the click target;
  // halls are only outlined inside them.
  zones = null,
  onZoneClick,             // (zone) => void
}) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const view = useRef({ scale: 1, x: 0, y: 0, dragging: false, moved: false, start: null });
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const venueLayoutUrl = venueLayoutUrlFor(sessionStorage.getItem("sessionExhibitionID"));
  const [stage, setStage] = useState({ w: 0, h: 0, left: 0, top: 0 });
  const [hover, setHover] = useState(null);

  // Fetched once up front for the loading / error state; the <image> below
  // then draws it from the browser cache.
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => { if (!cancelled) setLoaded(true); };
    probe.onerror = () => {
      if (cancelled) return;
      console.warn(`[VenueView] venue map not found at ${venueLayoutUrl}`);
      setFailed(true);
    };
    probe.src = venueLayoutUrl;
    return () => { cancelled = true; probe.onload = null; probe.onerror = null; };
  }, [venueLayoutUrl]);

  // Fit the drawing inside the box (contain), centred.
  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    const ratio = VENUE_VIEWBOX.width / VENUE_VIEWBOX.height;
    const w = Math.min(W, H * ratio);
    const h = w / ratio;
    setStage({ w, h, left: (W - w) / 2, top: (H - h) / 2 });
  }, []);

  const apply = useCallback(() => {
    const { scale, x, y } = view.current;
    if (stageRef.current) stageRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  }, []);

  const reset = useCallback(() => {
    view.current.scale = 1;
    view.current.x = 0;
    view.current.y = 0;
    apply();
  }, [apply]);

  useEffect(() => {
    fit();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  // Wheel zoom around the cursor. A native, non-passive listener so the page
  // does not scroll underneath.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left - stage.left;
      const py = e.clientY - rect.top - stage.top;
      const v = view.current;
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      const k = next / v.scale;
      v.x = px - (px - v.x) * k;
      v.y = py - (py - v.y) * k;
      v.scale = next;
      if (next === MIN_SCALE) { v.x = 0; v.y = 0; }
      apply();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [apply, stage.left, stage.top]);

  const onMouseDown = (e) => {
    view.current.dragging = true;
    view.current.moved = false;
    view.current.start = { x: e.clientX - view.current.x, y: e.clientY - view.current.y };
  };
  const onMouseMove = (e) => {
    const v = view.current;
    if (!v.dragging || v.scale === MIN_SCALE) return;
    const nx = e.clientX - v.start.x;
    const ny = e.clientY - v.start.y;
    if (Math.abs(nx - v.x) + Math.abs(ny - v.y) > 2) v.moved = true;
    v.x = nx;
    v.y = ny;
    apply();
  };
  const onMouseUp = () => { view.current.dragging = false; };

  const selectedKey = String(selectedHallName || "").trim().toLowerCase();

  return (
    <div
      ref={containerRef}
      className="position-relative"
      style={{ width: "100%", height: `${height}px`, overflow: "hidden", backgroundColor: "#f8f9fa", cursor: "grab", userSelect: "none" }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onDoubleClick={reset}
    >
      {!loaded && !failed && (
        <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ zIndex: 2 }}>
          <div className="spinner-border text-primary mb-2" role="status" />
          <div className="small text-muted">Loading venue map...</div>
        </div>
      )}
      {failed && (
        <div className="position-absolute top-50 start-50 translate-middle small text-danger" style={{ zIndex: 2 }}>
          The venue map could not be loaded.
        </div>
      )}

      <div
        style={{ position: "absolute", left: stage.left, top: stage.top, width: stage.w, height: stage.h }}
      >
        <div ref={stageRef} style={{ width: "100%", height: "100%", transformOrigin: "0 0" }}>
          {loaded && (
            <svg
              viewBox={`0 0 ${VENUE_VIEWBOX.width} ${VENUE_VIEWBOX.height}`}
              style={{ display: "block", width: "100%", height: "100%" }}
            >
              {/* The drawing sits in the overlay's own viewBox, so the halls are
                  placed in the same units as the picture and page CSS cannot
                  size the two apart. */}
              <image
                href={venueLayoutUrl}
                x={0}
                y={0}
                width={VENUE_VIEWBOX.width}
                height={VENUE_VIEWBOX.height}
                preserveAspectRatio="none"
                pointerEvents="none"
              />
              {zones && zones.map((zone) => {
                const color = zone.status ? getStatusColor(zone.status) : UNALLOCATED_FILL;
                const stroke = strokeFor(color);
                const hovered = hover === `zone:${zone.id}`;
                const { x, y, w, h } = zone.box;
                const fs = Math.max(18 * U, Math.min(34 * U, Math.min(w, h) * 0.07));
                const cx = x + w / 2;
                const cy = y + h / 2;
                const sub = `${zone.status || "No data"} · ${zone.hallCount} halls`;
                const badgeW = Math.max(zone.label.length, sub.length) * fs * 0.6 + fs;
                const badgeH = fs * 2.6;
                return (
                  <g
                    key={`zone:${zone.id}`}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover(`zone:${zone.id}`)}
                    onMouseLeave={() => setHover(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (view.current.moved) return;
                      if (onZoneClick) onZoneClick(zone);
                    }}
                  >
                    <title>{`${zone.label}: ${zone.status || "No data"} - click for details`}</title>
                    {zone.shape ? (
                      <>
                        <path d={zone.shape.fill} fill={color} fillOpacity={hovered ? 0.34 : 0.22} />
                        <path d={zone.shape.outline} fill="none" stroke={stroke}
                          strokeWidth={(hovered ? 9 : 6) * U} strokeLinecap="square" pointerEvents="none" />
                      </>
                    ) : (
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        rx={12 * U}
                        fill={color}
                        fillOpacity={hovered ? 0.34 : 0.22}
                        stroke={stroke}
                        strokeWidth={(hovered ? 9 : 6) * U}
                      />
                    )}
                    <rect x={cx - badgeW / 2} y={cy - badgeH / 2} width={badgeW} height={badgeH} rx={fs * 0.3}
                      fill="#ffffff" fillOpacity="0.95" stroke={stroke} strokeWidth={3 * U} />
                    <text x={cx} y={cy - fs * 0.1} textAnchor="middle" fontSize={fs} fontWeight="bold" fill="#111827">
                      {zone.label}
                    </text>
                    <text x={cx} y={cy + fs * 1.05} textAnchor="middle" fontSize={fs * 0.8} fill="#374151">
                      {sub}
                    </text>
                  </g>
                );
              })}
              {zones && VENUE_HALLS.map((hall) => (
                // Outline + name only; the zone underneath takes the click.
                <g key={hall.hallName} pointerEvents="none">
                  <HallShape hall={hall}
                    fill="none" stroke="#475569" strokeWidth={2 * U} strokeDasharray={`${8 * U} ${5 * U}`} />
                  <text x={hall.x + hall.w / 2} y={hall.y + 22 * U} textAnchor="middle" fontSize={16 * U} fontWeight="bold" fill="#1f2937">
                    {hall.label}
                  </text>
                </g>
              ))}
              {!zones && VENUE_HALLS.map((hall) => {
                const status = statusOfHall ? statusOfHall(hall.hallName) : null;
                const color = status ? getStatusColor(status) : UNALLOCATED_FILL;
                const stroke = strokeFor(color);
                const selected = selectedKey && selectedKey === hall.hallName.toLowerCase();
                const hovered = hover === hall.hallName;
                // Badge text sized to the hall, within readable bounds.
                const fs = Math.max(11 * U, Math.min(26 * U, Math.min(hall.w, hall.h) * 0.16));
                const cx = hall.x + hall.w / 2;
                const cy = hall.y + hall.h / 2;
                const badgeW = Math.min(hall.w - 6, Math.max(hall.label.length, (status || "No data").length) * fs * 0.6 + fs);
                const badgeH = fs * 2.5;
                return (
                  <g
                    key={hall.hallName}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover(hall.hallName)}
                    onMouseLeave={() => setHover(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (view.current.moved) return; // end of a drag, not a click
                      if (onHallClick) onHallClick(hall);
                    }}
                  >
                    <title>{`${hall.label}: ${status || "No data"}`}</title>
                    <HallShape
                      hall={hall}
                      fill={color}
                      fillOpacity={selected || hovered ? 0.55 : 0.38}
                      stroke={stroke}
                      strokeWidth={(selected ? 7 : 3) * U}
                    />
                    <rect
                      x={cx - badgeW / 2}
                      y={cy - badgeH / 2}
                      width={badgeW}
                      height={badgeH}
                      rx={fs * 0.3}
                      fill="#ffffff"
                      fillOpacity="0.95"
                      stroke={stroke}
                      strokeWidth={2 * U}
                    />
                    <text x={cx} y={cy - fs * 0.15} textAnchor="middle" fontSize={fs} fontWeight="bold" fill="#111827">
                      {hall.label}
                    </text>
                    <text x={cx} y={cy + fs * 1.0} textAnchor="middle" fontSize={fs * 0.85} fill="#374151">
                      {status || "No data"}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>

      <div className="position-absolute small text-muted bg-white border rounded px-2 py-1" style={{ right: 8, bottom: 8, zIndex: 2, fontSize: "11px" }}>
        Scroll to zoom · drag to pan · double-click to fit · {zones ? "click a venue zone for its details" : "click a hall to open it"}
      </div>
    </div>
  );
};

export default VenueView;
